import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

const Hero = ({ onBookClick }) => {
  const { t } = useLanguage();

  const scrollToTours = () => {
    const element = document.querySelector('#tours');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="home" 
      className="relative h-screen w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Video/Image Background */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=1920&q=80"
          alt="Madrid cityscape"
          className="w-full h-full object-cover"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl"
        >
          {/* Overline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xs uppercase tracking-[0.3em] text-white/80 mb-4 font-body"
          >
            Tours privados en tuk-tuk eléctrico
          </motion.p>

          {/* Main Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6"
            data-testid="hero-title"
          >
            {t.hero.title}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="text-lg sm:text-xl lg:text-2xl text-white/90 font-light mb-3 font-body"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          {/* Author signature */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="text-sm sm:text-base text-white/60 italic font-light mb-10 tracking-wide"
            data-testid="hero-signature"
          >
            By Carlos Casal
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
          >
            <Button
              onClick={onBookClick}
              size="lg"
              className="bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-full px-10 py-6 text-lg font-semibold shadow-2xl hover:shadow-[#C2410C]/30 transition-all duration-300 hover:scale-105 btn-shine"
              data-testid="hero-cta-button"
            >
              {t.hero.cta}
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          onClick={scrollToTours}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 hover:text-white transition-colors"
          data-testid="scroll-indicator"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown className="h-8 w-8" />
          </motion.div>
        </motion.button>
      </div>

      {/* Bottom gradient for smooth transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FDFBF7] to-transparent" />
    </section>
  );
};

export default Hero;
